import React, { useEffect, useState, useCallback } from 'react'
import '../../assets/theme/theme.less'
import './saques.css'
import { LayoutComponent as Layout, TitleTabs as Title, TabsMenuComponent as TabsMenu } from '../../components'
import { Row, Col, Tabs, message as alert } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { 
    getSaques, 
    getSaqueDetails, 
    approveSaque, 
    rejectSaque,
    finalizeSaque,
    resetSuccessAction,
    resetErrorMessage
} from '../../redux/slices/saquesSlice'
import { getNotifications } from '../../redux/slices/monitorsSlice'
import { useLocation } from 'react-router-dom'

import SaquesTable from './saquesTable'
import SaqueDetailsModal from './saqueDetailsModal'
import RejectSaqueModal from './rejectSaqueModal'
import FinalizeSaqueModal from './finalizeSaqueModal'

const { TabPane } = Tabs

const Saques = () => {
    const dispatch = useDispatch()
    
    // State variables
    const [detailsModalVisible, setDetailsModalVisible] = useState(false)
    const [rejectModalVisible, setRejectModalVisible] = useState(false)
    const [finalizeModalVisible, setFinalizeModalVisible] = useState(false)
    const [selectedSaqueId, setSelectedSaqueId] = useState(null)
    const [currentStatus, setCurrentStatus] = useState('pending')
    const [statusCounts, setStatusCounts] = useState({
        pending: 0,
        approved: 0,
        rejected: 0,
        finalized: 0
    })
    
    // Redux state
    const { 
        isLoading, 
        saques, 
        saque,
        message, 
        isSubmit, 
        successAction
    } = useSelector(state => state.saques)
    const location = useLocation()
    const isHistoryPage = location.pathname.includes('historico')
    
    // Fetch saques with specific status
    const fetchSaques = useCallback((status) => {
        setCurrentStatus(status)
        
        // Se status for 'all', buscar todos os saques (sem filtro)
        if (status === 'all') {
            dispatch(getSaques({ tipo: 'balance' }))
        } else {
            dispatch(getSaques({ status, tipo: 'balance' }))
        }
    }, [dispatch])
    
    // Initial data fetch
    useEffect(() => {
        if (isHistoryPage) {
            // Na página de histórico, buscar todos os saques (sem filtro de status)
            fetchSaques('all')
        } else {
            fetchSaques('pending')
        }
        // Buscar notificações globais
        dispatch(getNotifications())
    }, [isHistoryPage, dispatch, fetchSaques])
    
    // Handle error messages separately
    useEffect(() => {
        if (message) {
            alert.error(message)
            
            // Reset error message after displaying it
            setTimeout(() => {
                dispatch(resetErrorMessage())
            }, 3000)
        }
    }, [message, dispatch])
    
    // Handle success actions separately
    useEffect(() => {
        if (successAction) {
            fetchSaques(currentStatus)
            setDetailsModalVisible(false)
            setRejectModalVisible(false)
            setFinalizeModalVisible(false)
            alert.success('Operação realizada com sucesso!')
            dispatch(resetSuccessAction())
            // Atualizar notificações
            dispatch(getNotifications())
        }
    }, [successAction, currentStatus, dispatch, fetchSaques])
    
    // Atualizar contadores quando saques forem carregados
    useEffect(() => {
        if (saques && saques.length >= 0) {
            const pending = saques.filter(s => s.status === 0 || s.status === 'pending').length
            const approved = saques.filter(s => s.status === 1 || s.status === 'approved').length
            const rejected = saques.filter(s => s.status === 4 || s.status === 'rejected').length
            const finalized = saques.filter(s => s.status === 3 || s.status === 'finalized').length
            
            setStatusCounts({
                pending,
                approved,
                rejected,
                finalized
            })
        }
    }, [saques])
    
    // Handle tab change
    const handleTabChange = (key) => {
        let status
        switch (key) {
            case '1':
                status = 0 // Pendente
                break
            case '2':
                status = 1 // Aprovado
                break
            case '3':
                status = 4 // Rejeitado
                break
            case '4':
                status = 3 // Finalizado
                break
            default:
                status = 0 // Pendente
        }
        fetchSaques(status)
    }
    
    // Details modal handlers
    const showDetailsModal = (id) => {
        setSelectedSaqueId(id)
        dispatch(getSaqueDetails(id))
        setDetailsModalVisible(true)
    }
    
    const handleDetailsModalOk = () => {
        setDetailsModalVisible(false)
    }
    
    const handleDetailsModalCancel = () => {
        setDetailsModalVisible(false)
    }
    
    // Approve saque
    const handleApprove = (id) => {
        dispatch(approveSaque(id))
    }
    
    // Reject modal handlers
    const showRejectModal = (id) => {
        setSelectedSaqueId(id)
        setRejectModalVisible(true)
    }
    
    const handleRejectModalCancel = () => {
        setRejectModalVisible(false)
    }
    
    const handleReject = (id, motivo) => {
        dispatch(rejectSaque({ id, motivo }))
    }
    
    // Finalize modal handlers
    const showFinalizeModal = (id) => {
        setSelectedSaqueId(id)
        setFinalizeModalVisible(true)
    }
    
    const handleFinalizeModalCancel = () => {
        setFinalizeModalVisible(false)
    }
    
    const handleFinalize = (id, comprovante) => {
        dispatch(finalizeSaque({ id, comprovante }))
    }
    
    // Usar contadores do estado
    const pendingCount = statusCounts.pending
    const approvedCount = statusCounts.approved
    const rejectedCount = statusCounts.rejected
    const finalizedCount = statusCounts.finalized
    
    return (
        <Layout width>
            <Row>
                <Col md={18}>
                    <h1 className="title page-title">Solicitações de Saque</h1>
                </Col>
            </Row>
            <Row className="mt-15 d-flex align-center">
                <Col md={24} className="d-flex align-center">
                    <div className="d-flex align-center m-auto">
                        <TabsMenu type="saques" />
                    </div>
                </Col>
            </Row>
            {!isHistoryPage ? (
                <Row className="mt-15" gutter={[15,15]}>
                    <Col span={24}>
                        <Tabs defaultActiveKey="1" centered onChange={handleTabChange}>
                            <TabPane tab={<Title title="Pendentes" notification={pendingCount} />} key="1">
                                <Row className="mt-15">
                                    <Col span={24} id="saques-table">
                                        <SaquesTable 
                                            data={saques} 
                                            loading={isLoading}
                                            showDetailsModal={showDetailsModal}
                                            handleApprove={handleApprove}
                                            handleReject={showRejectModal}
                                            handleFinalize={showFinalizeModal}
                                            refreshList={() => {
                                                fetchSaques(currentStatus);
                                                dispatch(getNotifications());
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </TabPane>
                            <TabPane tab={<Title title="Aprovadas" notification={approvedCount} />} key="2">
                                <Row className="mt-15">
                                    <Col span={24} id="saques-table">
                                        <SaquesTable 
                                            data={saques} 
                                            loading={isLoading}
                                            showDetailsModal={showDetailsModal}
                                            handleApprove={handleApprove}
                                            handleReject={showRejectModal}
                                            handleFinalize={showFinalizeModal}
                                            refreshList={() => {
                                                fetchSaques(currentStatus);
                                                dispatch(getNotifications());
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </TabPane>
                            <TabPane tab={<Title title="Rejeitadas" notification={rejectedCount} />} key="3">
                                <Row className="mt-15">
                                    <Col span={24} id="saques-table">
                                        <SaquesTable 
                                            data={saques} 
                                            loading={isLoading}
                                            showDetailsModal={showDetailsModal}
                                            handleApprove={handleApprove}
                                            handleReject={showRejectModal}
                                            handleFinalize={showFinalizeModal}
                                            refreshList={() => {
                                                fetchSaques(4);
                                                dispatch(getNotifications());
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </TabPane>
                            <TabPane tab={<Title title="Finalizadas" notification={finalizedCount} />} key="4">
                                <Row className="mt-15">
                                    <Col span={24} id="saques-table">
                                        <SaquesTable 
                                            data={saques} 
                                            loading={isLoading}
                                            showDetailsModal={showDetailsModal}
                                            handleApprove={handleApprove}
                                            handleReject={showRejectModal}
                                            handleFinalize={showFinalizeModal}
                                            refreshList={() => {
                                                fetchSaques(currentStatus);
                                                dispatch(getNotifications());
                                            }}
                                        />
                                    </Col>
                                </Row>
                            </TabPane>
                        </Tabs>
                    </Col>
                </Row>
            ) : (
                <Row className="mt-15" gutter={[15,15]}>
                    <Col span={24}>
                        <SaquesTable 
                            data={saques} 
                            loading={isLoading}
                            showDetailsModal={showDetailsModal}
                            handleApprove={handleApprove}
                            handleReject={showRejectModal}
                            handleFinalize={showFinalizeModal}
                            refreshList={() => {
                                fetchSaques('all');
                                dispatch(getNotifications());
                            }}
                            isHistoryMode={true}
                        />
                    </Col>
                </Row>
            )}
            
            {/* Modals */}
            <SaqueDetailsModal 
                visible={detailsModalVisible}
                saque={saque}
                loading={isLoading}
                handleOk={handleDetailsModalOk}
                handleCancel={handleDetailsModalCancel}
                handleApprove={handleApprove}
                handleReject={showRejectModal}
                handleFinalize={showFinalizeModal}
                isSubmit={isSubmit}
            />
            
            <RejectSaqueModal 
                visible={rejectModalVisible}
                handleCancel={handleRejectModalCancel}
                handleReject={handleReject}
                saqueId={selectedSaqueId}
                isSubmit={isSubmit}
            />
            
            <FinalizeSaqueModal 
                visible={finalizeModalVisible}
                handleCancel={handleFinalizeModalCancel}
                handleFinalize={handleFinalize}
                saqueId={selectedSaqueId}
                isSubmit={isSubmit}
            />
        </Layout>
    )
}

export default Saques 